import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Same reconciliation shape as `usePendingMTTracking`, generalized to any
 * keyed list backed by a REST create/delete (no MT queue, no mtmsn to match
 * on) — e.g. the Contacts list. Solves the same class of problem: after a
 * successful add/remove, the *next* poll of the parent resource can return a
 * snapshot that hasn't caught up yet (server-side propagation lag), which
 * would otherwise make a just-added item flash in and then disappear, or a
 * just-removed item reappear, until a later poll finally reflects the write.
 *
 * `addOptimistic`/`removeOptimistic` hold the local intent until the server
 * list agrees (or up to a 10s grace period, matching the constant used
 * throughout `usePendingMTTracking`, in case the write never shows up).
 */
const GRACE_PERIOD_MS = 10_000;

interface PendingAdd<T> {
  item: T;
  addedAt: number;
}

function reconcile<T>(
  pendingAdds: Map<string, PendingAdd<T>>,
  pendingRemoves: Map<string, number>,
  rawList: T[],
  keyFn: (item: T) => string,
): T[] {
  const now = Date.now();
  const serverKeys = new Set(rawList.map(keyFn));

  for (const [key, add] of pendingAdds) {
    if (serverKeys.has(key) || now - add.addedAt > GRACE_PERIOD_MS) {
      pendingAdds.delete(key);
    }
  }
  for (const [key, removedAt] of pendingRemoves) {
    if (!serverKeys.has(key) || now - removedAt > GRACE_PERIOD_MS) {
      pendingRemoves.delete(key);
    }
  }

  const merged = rawList.filter((item) => !pendingRemoves.has(keyFn(item)));
  for (const add of pendingAdds.values()) {
    if (!merged.some((item) => keyFn(item) === keyFn(add.item))) merged.push(add.item);
  }
  return merged;
}

export function useOptimisticList<T>(serverList: T[], keyFn: (item: T) => string) {
  const pendingAddsRef = useRef<Map<string, PendingAdd<T>>>(new Map());
  const pendingRemovesRef = useRef<Map<string, number>>(new Map());
  const [mergedList, setMergedList] = useState<T[]>(serverList);

  useEffect(() => {
    setMergedList(reconcile(pendingAddsRef.current, pendingRemovesRef.current, serverList, keyFn));
  }, [serverList, keyFn]);

  const addOptimistic = useCallback(
    (item: T) => {
      pendingAddsRef.current.set(keyFn(item), { item, addedAt: Date.now() });
      setMergedList(reconcile(pendingAddsRef.current, pendingRemovesRef.current, serverList, keyFn));
    },
    [serverList, keyFn],
  );

  const removeOptimistic = useCallback(
    (key: string) => {
      pendingRemovesRef.current.set(key, Date.now());
      setMergedList(reconcile(pendingAddsRef.current, pendingRemovesRef.current, serverList, keyFn));
    },
    [serverList, keyFn],
  );

  return { mergedList, addOptimistic, removeOptimistic };
}
